import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { type ScratchRewardType } from '@/common/types/scratch';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import {
  H2,
  StringSmall,
  StringXSmall,
} from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { Picture } from '@/components/ui/Picture/Picture';
import { claimAndClose } from '@/redux/reducers/scratchReducer';
import { addToBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import lightPng from '/img/patterns/light.png';
import lightWebp from '/img/patterns/light.webp';

const STATIC_IMG: Partial<Record<ScratchRewardType, string>> = {
  goldTicket: '/img/icons/ticket-gold.webp',
  silverTicket: '/img/icons/ticket-silver.webp',
  diamondTicket: '/img/icons/ticket-diamond.webp',
  starPoints: '/img/icons/star-large.png',
  bingoBalls: '/img/bingo/bingo-icon.png',
};

const CURRENCY_MAP: Partial<
  Record<ScratchRewardType, 'tgem' | 'cash' | 'dice' | 'star'>
> = {
  tgem: 'tgem',
  cash: 'cash',
  dice: 'dice',
};

const REWARD_LABELS: Record<ScratchRewardType, string> = {
  tgem: 'T-Gems',
  cash: 'Cash',
  dice: 'Dice',
  starPoints: 'Star Points',
  goldTicket: 'Gold Ticket',
  silverTicket: 'Silver Ticket',
  diamondTicket: 'Diamond Ticket',
  bingoBalls: 'Bingo Balls',
};

const REWARD_TO_BALANCE: Record<string, string> = {
  cash: 'cash',
  tgem: 'tgem',
  dice: 'dice',
  starPoints: 'point',
  bingoBalls: 'bingoBalls',
};

interface ScratchRewardItemProps {
  rewardType: ScratchRewardType;
  value: number;
}

const ScratchRewardItem: FC<ScratchRewardItemProps> = ({
  rewardType,
  value,
}) => {
  const currencyKind = CURRENCY_MAP[rewardType];
  const imgSrc = !currencyKind ? (STATIC_IMG[rewardType] ?? null) : null;

  return (
    <div className="flex flex-col items-center gap-1">
      {currencyKind ? (
        <CurrencyImage kind={currencyKind} size="64px" />
      ) : imgSrc ? (
        <img
          src={imgSrc}
          alt={rewardType}
          className="w-16 h-16 object-contain"
        />
      ) : null}
      <H2 weight="extrabold" className="text-white leading-none">
        +{value}
      </H2>
      <StringXSmall className="text-white/50">
        {REWARD_LABELS[rewardType] ?? rewardType.replace(/_/g, ' ')}
      </StringXSmall>
    </div>
  );
};

/**
 * Prize claim modal — modeled after BingoLineRewardsModal:
 *  • full-screen gradient backdrop
 *  • light pattern overlay
 *  • reward animates in with spring
 *  • Confetti runs on Claim, then `onComplete` adds to balance and closes
 */
const ScratchPrizeModal: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isClaiming, setIsClaiming] = useState(false);

  const active = useAppSelector((s) => s.scratch.active);
  const pendingId = useAppSelector((s) => s.scratch.pendingClaimTicketId);

  // Server reveal returns status 'claimed' immediately (prize already
  // credited). Match purely by id — `pendingClaimTicketId` is only set
  // after a successful reveal, so the status branch isn't needed.
  const ticket =
    pendingId !== null
      ? (active.find((t) => t.id === pendingId) ?? null)
      : null;
  const isVisible = !!ticket && !!ticket.prize;
  const reward = ticket?.prize ?? null;
  const matchCount = ticket?.matchCount ?? 1;

  const headline =
    matchCount === 3
      ? t('TRIPLE WIN!')
      : matchCount === 2
        ? t('Double match!')
        : t('Match!');
  const subhead =
    matchCount === 3
      ? t('All three cells matched — 3× reward!')
      : matchCount === 2
        ? t('Two cells matched — 2× reward')
        : t('Guaranteed reward unlocked');

  const handleClaim = (): void => {
    setIsClaiming(true);
  };

  const handleConfettiComplete = (): void => {
    if (reward) {
      const balanceKey = REWARD_TO_BALANCE[reward.rewardType];

      if (balanceKey) {
        const balanceUpdate: Record<string, number> = {
          [balanceKey]: reward.value,
        };
        dispatch(addToBalance(balanceUpdate));
      }
    }
    setIsClaiming(false);
    dispatch(claimAndClose());
  };

  return (
    <AnimatePresence>
      {isVisible && reward && (
        <motion.div
          className="fixed inset-0 flex flex-col items-center justify-center z-[9999] px-6"
          style={{
            background:
              'linear-gradient(180deg, rgba(24,26,32,0) 0%, rgba(24,26,32,0.85) 25%, #181a20 55%, #181a20 100%)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {isClaiming && (
            <Confetti
              className="inset-0 absolute"
              loop={false}
              onComplete={handleConfettiComplete}
              speed={1.5}
            />
          )}

          <Picture
            img={lightPng}
            webp={lightWebp}
            alt=""
            className="absolute w-full h-max [&>img]:h-auto mix-blend-overlay -z-10 pointer-events-none"
          />

          <motion.div
            className="flex flex-col items-center mb-6"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
          >
            <H2
              weight="black"
              className="text-white text-center"
              style={{ letterSpacing: '0.04em' }}
            >
              {headline}
            </H2>
            <StringSmall className="text-white/65 text-center mt-1">
              {subhead}
            </StringSmall>
          </motion.div>

          <div className="flex items-end justify-center gap-6 flex-wrap mb-8">
            <motion.div
              key={reward.rewardType + '-' + reward.value}
              initial={{ opacity: 0, y: 20, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                delay: 0.15,
                duration: 0.35,
                ease: [0.34, 1.56, 0.64, 1],
              }}
            >
              <ScratchRewardItem
                rewardType={reward.rewardType}
                value={reward.value}
              />
            </motion.div>
          </div>

          <motion.div
            className="w-full"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            <MainButton
              text={t('Claim reward')}
              onClick={handleClaim}
              disabled={isClaiming}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { ScratchPrizeModal };
